// @ts-check
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import {
  formatDay,
  label,
  nextJanuaryFifth,
  PARK_NEXT_ACTION,
  WHOLESALE_OUTREACH_KIND,
  WHOLESALE_OUTREACH_STATUS,
  WHOLESALE_STAGE,
} from '../../utils/wholesale';
import { card, DeadlineBadge, input, primaryBtn, secondaryBtn } from './shared';

// Statuses the server persists come from the shared constants; these three are derived by the queue.
const DERIVED = Object.freeze({ READY: 'ready', MANUAL: 'manual', BLOCKED: 'blocked', APPLICATION: 'application' });
// Defence in depth: the server only stores http(s) links, and the page only ever links to those.
// A URL with credentials in it (https://user@host) is shown as text, never linked.
const SAFE_LINK = /^https?:\/\/(?![^\s/?#]*@)[^\s]+$/i;
const POLL_MS = 10000;

// The three templates wholesale outreach sends; mirrors services/wholesale_whatsapp_templates.py.
const WHOLESALE_TEMPLATES = Object.freeze([
  ['wholesale_stockist_intro', 'Stockist and boutique first message'],
  ['wholesale_curator_intro', 'Curator, festival and venue first message'],
  ['wholesale_followup', 'Follow-up (day 4 and day 10)'],
]);
const TEMPLATE_CHIP = Object.freeze({
  missing: ['Missing', 'bg-red-100 text-red-800'],
  pending: ['Pending review', 'bg-amber-100 text-amber-800'],
  approved: ['Approved', 'bg-green-100 text-green-800'],
  rejected: ['Rejected', 'bg-red-100 text-red-800'],
  not_approved: ['Not approved', 'bg-amber-100 text-amber-800'],
});
const templateState = (map, name) => (map && map[name]) || 'missing';
const OUTCOME_TOAST = Object.freeze({
  submitted: 'submitted to Meta for review',
  already_exists: 'already exists on WhatsApp',
});

/**
 * The three wholesale WhatsApp templates and their state, with one button that submits the missing ones to Meta.
 * @param {{states: Record<string, string>|undefined, configured: boolean, busy: boolean, onSubmit: () => Promise<void>}} props
 */
const TemplatesStrip = ({ states, configured, busy, onSubmit }) => {
  const [confirming, setConfirming] = useState(false);
  const missing = WHOLESALE_TEMPLATES.filter(([name]) => templateState(states, name) === 'missing').length;

  const confirm = async () => {
    await onSubmit();
    setConfirming(false);
  };

  return (
    <div className={`${card} p-4 space-y-3`} data-testid="templates-strip">
      <h3 className="text-base font-medium">WhatsApp templates</h3>
      <ul className="space-y-1">
        {WHOLESALE_TEMPLATES.map(([name, what]) => {
          const [text, tone] = TEMPLATE_CHIP[templateState(states, name)] || TEMPLATE_CHIP.not_approved;
          return (
            <li key={name} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span>
                <span className="font-medium">{name}</span> <span className="text-muted-foreground">{what}</span>
              </span>
              <span className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium ${tone}`}>{text}</span>
            </li>
          );
        })}
      </ul>
      {!confirming ? (
        <div>
          <button
            className={secondaryBtn}
            disabled={busy || missing === 0 || !configured}
            title={!configured ? 'WhatsApp is not configured on this environment' : undefined}
            onClick={() => setConfirming(true)}
          >
            Submit missing templates to Meta
          </button>
        </div>
      ) : (
        <div className="border border-border rounded-lg p-3 space-y-3">
          <p className="text-sm">
            This submits up to {missing} template{missing === 1 ? '' : 's'} to Meta for review under your WhatsApp
            Business account. Meta usually reviews within a few minutes to a day.
          </p>
          <div className="flex gap-2">
            <button className={primaryBtn} disabled={busy} onClick={confirm}>
              Confirm
            </button>
            <button className={secondaryBtn} disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
const PLACEHOLDER = /\{\{\s*\w+\s*\}\}/g;

// Clipboard API first, then the old execCommand route; false when neither works.
const copyText = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the textarea route
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
    area.remove();
    return !!ok;
  } catch {
    return false;
  }
};

const bumpCap = (cap, status) =>
  cap && status !== WHOLESALE_OUTREACH_STATUS.FAILED
    ? { ...cap, used: cap.used + 1, remaining: Math.max(cap.remaining - 1, 0) }
    : cap;

const FOLLOWUP_LABEL = Object.freeze({
  [WHOLESALE_OUTREACH_KIND.NUDGE]: 'Nudge (day 4)',
  [WHOLESALE_OUTREACH_KIND.FINAL]: 'Final line (day 10)',
});

/** Row identity: a partner can have a nudge and a final in the list at the same time. */
const rowKeyOf = (item) => (FOLLOWUP_LABEL[item.kind] ? `${item.partner_id}:${item.kind}` : item.partner_id);
const sameRow = (a, b) => a.partner_id === b.partner_id && a.kind === b.kind;

const overdueText = (days) => {
  if (!days || days < 1) return 'Due today';
  return `${days} day${days === 1 ? '' : 's'} overdue`;
};

const STATUS_BADGE = {
  [DERIVED.READY]: 'bg-green-100 text-green-800',
  [WHOLESALE_OUTREACH_STATUS.SENDING]: 'bg-amber-100 text-amber-800',
  [WHOLESALE_OUTREACH_STATUS.QUEUED]: 'bg-amber-100 text-amber-800',
  [WHOLESALE_OUTREACH_STATUS.SENT]: 'bg-muted text-muted-foreground',
  [WHOLESALE_OUTREACH_STATUS.FAILED]: 'bg-red-100 text-red-800',
  [DERIVED.MANUAL]: 'bg-muted text-muted-foreground',
  [DERIVED.APPLICATION]: 'bg-muted text-muted-foreground',
  [DERIVED.BLOCKED]: 'bg-red-100 text-red-800',
};

/**
 * A partner that has to be contacted by hand: the text to send, the details to send it to, and a control to
 * record that it was sent. Nothing here sends anything.
 * @param {{item: any, busy: boolean, onMark: (item: any, channel: string, note: string) => Promise<void>}} props
 */
const ManualHelp = ({ item, busy, onMark }) => {
  const toast = useToast();
  const options = item.manual_channel_options || [];
  const [marking, setMarking] = useState(false);
  const [channel, setChannel] = useState('');
  const [note, setNote] = useState('');

  const copy = async () => {
    if (await copyText(item.manual_draft)) toast.success('Draft copied');
    else toast.error('Could not copy; select the text and copy it by hand');
  };

  const confirm = async () => {
    await onMark(item, channel, note.trim());
    setMarking(false);
    setChannel('');
    setNote('');
  };

  if (options.length === 0) return null;
  return (
    <div className="space-y-2">
      {(item.manual_handle || item.manual_phone) && (
        <p className="text-sm">
          {item.manual_handle && (
            <span>
              Instagram: <span className="font-medium">{item.manual_handle}</span>
            </span>
          )}
          {item.manual_handle && item.manual_phone && ' · '}
          {item.manual_phone && (
            <span>
              Phone: <span className="font-medium">{item.manual_phone}</span>
            </span>
          )}
        </p>
      )}
      {item.manual_draft && (
        <>
          <textarea className={input} aria-label="Draft message" rows={5} readOnly value={item.manual_draft} />
          <button className={secondaryBtn} onClick={copy}>
            Copy
          </button>
        </>
      )}
      {!marking ? (
        <div>
          <button className={primaryBtn} disabled={busy} onClick={() => setMarking(true)}>
            Mark as sent
          </button>
        </div>
      ) : (
        <div className="border border-border rounded-lg p-3 space-y-3">
          <p className="text-sm">Record that you sent this yourself. Nothing is sent from here.</p>
          <select
            aria-label="Sent via"
            className={`${input} max-w-xs`}
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
          >
            <option value="">Choose how you sent it</option>
            {options.map((c) => (
              <option key={c} value={c}>
                {label(c)}
              </option>
            ))}
          </select>
          <input
            className={input}
            aria-label="Note"
            placeholder="Note (optional)"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex gap-2">
            <button className={primaryBtn} disabled={busy || !channel} onClick={confirm}>
              Confirm
            </button>
            <button className={secondaryBtn} disabled={busy} onClick={() => setMarking(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * A partner that takes applications through a web form: the link, what they ask for, and a control to record that
 * it was submitted. The ticks are only a reminder for this visit and are not saved. Nothing here sends anything.
 * @param {{item: any, busy: boolean, onSubmit: (item: any, note: string) => Promise<void>}} props
 */
const ApplicationHelp = ({ item, busy, onSubmit }) => {
  const [marking, setMarking] = useState(false);
  const [note, setNote] = useState('');
  const [ticked, setTicked] = useState(/** @type {Record<number, boolean>} */ ({}));
  const url = item.application_url || '';
  const lines = item.application_checklist || [];

  const confirm = async () => {
    await onSubmit(item, note.trim());
    setMarking(false);
    setNote('');
  };

  return (
    <div className="space-y-3">
      <p className="text-sm">
        Apply through their form, not by email: <span className="font-medium break-all">{url}</span>
      </p>
      {SAFE_LINK.test(url) && (
        <a className={`${secondaryBtn} inline-block`} href={url} target="_blank" rel="noopener noreferrer">
          Open application
        </a>
      )}
      {lines.length > 0 && (
        <div>
          <p className="text-sm text-muted-foreground mb-1">What they ask for</p>
          <ul className="space-y-1">
            {lines.map((line, index) => (
              <li key={`${index}-${line}`}>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={!!ticked[index]}
                    onChange={(e) => setTicked((prev) => ({ ...prev, [index]: e.target.checked }))}
                  />
                  <span>{line}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!marking ? (
        <div>
          <button className={primaryBtn} disabled={busy} onClick={() => setMarking(true)}>
            Mark as submitted
          </button>
        </div>
      ) : (
        <div className="border border-border rounded-lg p-3 space-y-3">
          <p className="text-sm">Record that you submitted the application yourself. Nothing is sent from here.</p>
          <input
            className={input}
            aria-label="Note"
            placeholder="Note (optional)"
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <div className="flex gap-2">
            <button className={primaryBtn} disabled={busy} onClick={confirm}>
              Confirm
            </button>
            <button className={secondaryBtn} disabled={busy} onClick={() => setMarking(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * One partner's draft. Email drafts are editable; WhatsApp text is fixed by the approved template.
 * A follow-up (item.kind nudge or final) is the same row with a kind label and an overdue badge; after a final
 * it also offers Park (with a confirm step).
 * @param {{item: any, fromEmails: string[], busyId: string|null, testingId: string|null, capReached: boolean, onSend: (item: any, edits: {subject: string, body: string}, fromEmail: string|null) => Promise<void>, onTest: (item: any, edits: {subject: string, body: string}, fromEmail: string) => Promise<void>, onMark?: (item: any, channel: string, note: string) => Promise<void>, onSubmit?: (item: any, note: string) => Promise<void>, onPark?: (item: any, parkOn: string) => Promise<void>}} props
 */
const OutreachRow = ({ item, fromEmails, busyId, testingId, capReached, onSend, onTest, onMark, onSubmit, onPark }) => {
  const rowKey = rowKeyOf(item);
  const followupLabel = FOLLOWUP_LABEL[item.kind];
  const [parkOn, setParkOn] = useState(/** @type {string|null} */ (null));
  const isEmail = item.channel === 'email';
  const isWhatsApp = item.channel === 'whatsapp';
  const [subject, setSubject] = useState(item.subject || '');
  const [body, setBody] = useState(item.body || '');
  const [confirming, setConfirming] = useState(false);
  const [from, setFrom] = useState('');
  const [testFrom, setTestFrom] = useState('');
  const sameOwner = item.same_owner_as || [];
  const isApplication = item.status === DERIVED.APPLICATION;
  const sendable = item.status === DERIVED.READY || item.status === WHOLESALE_OUTREACH_STATUS.FAILED;
  const showDraft = isEmail || isWhatsApp;
  const busy = busyId !== null || testingId !== null; // never two sends at once
  // The test can also be run on a row the email switch has blocked; it needs a draft to look at.
  const testable = isEmail && (sendable || item.status === DERIVED.BLOCKED) && !!body.trim();
  // {{date}}-style variables the server could not fill: the operator must resolve them before sending.
  const unresolved = isEmail && sendable ? [...new Set(`${subject} ${body}`.match(PLACEHOLDER) || [])] : [];
  const blockedByPlaceholders = unresolved.length > 0;

  const confirm = async () => {
    await onSend(item, { subject, body }, isEmail ? from : null);
    setConfirming(false);
    setFrom('');
  };

  return (
    <div className={`${card} p-4 space-y-3`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium">{item.name}</p>
          <p className="text-xs text-muted-foreground">
            {[item.city, label(item.kind), item.contact_name].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {followupLabel && (
            <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-muted text-muted-foreground">
              {followupLabel}
            </span>
          )}
          {followupLabel && item.due_on ? (
            <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap bg-amber-100 text-amber-800">
              {overdueText(item.days_overdue)}
            </span>
          ) : (
            !followupLabel && <DeadlineBadge item={item} />
          )}
          <span
            className={`inline-flex px-2.5 py-0.5 rounded-md text-xs font-medium ${STATUS_BADGE[item.status] || ''}`}
          >
            {label(item.status)}
          </span>
        </div>
      </div>

      {sameOwner.length > 0 && (
        <p className="text-sm rounded-md bg-amber-50 text-amber-900 px-3 py-2 dark:bg-amber-950/40 dark:text-amber-200">
          Same owner as {sameOwner.map((o) => `${o.name} (${label(o.stage)})`).join(', ')}. Consider one combined email,
          or space them out.
        </p>
      )}

      {item.reason && <p className="text-sm text-muted-foreground">{item.reason}</p>}
      {item.error && <p className="text-sm text-red-700">{item.error}</p>}

      {isApplication && onSubmit && <ApplicationHelp item={item} busy={busy} onSubmit={onSubmit} />}

      {item.status === DERIVED.MANUAL && onMark && <ManualHelp item={item} busy={busy} onMark={onMark} />}

      {showDraft && item.status !== DERIVED.MANUAL && (
        <div className="space-y-2">
          <p className="text-sm">
            {label(item.channel)} to <span className="font-medium">{item.address}</span>
          </p>
          {isEmail ? (
            <>
              <input
                className={input}
                aria-label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={!sendable}
              />
              <textarea
                className={input}
                aria-label="Message"
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                disabled={!sendable}
              />
            </>
          ) : (
            <p className="text-sm whitespace-pre-wrap bg-muted rounded-lg p-3">{item.body}</p>
          )}
          {blockedByPlaceholders && (
            <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {unresolved.map((p) => `Fill ${p}`).join(', ')} before sending
            </p>
          )}
          {item.mentions_attachment && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              This draft mentions an attachment. No attachments will be sent with this message.
            </p>
          )}
        </div>
      )}

      {testable && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Test from"
            className={`${input} max-w-xs`}
            value={testFrom}
            onChange={(e) => setTestFrom(e.target.value)}
          >
            <option value="">Choose a sender for the test</option>
            {fromEmails.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </select>
          <button
            className={secondaryBtn}
            disabled={busy || !testFrom}
            onClick={() => onTest(item, { subject, body }, testFrom)}
          >
            Send test to me
          </button>
        </div>
      )}

      {item.status !== DERIVED.MANUAL && !isApplication && (
        <div>
          {!confirming ? (
            <button
              className={primaryBtn}
              disabled={!sendable || busy || blockedByPlaceholders || capReached}
              title={capReached && sendable ? 'Daily send limit reached' : undefined}
              onClick={() => setConfirming(true)}
            >
              Send
            </button>
          ) : (
            <div className="border border-border rounded-lg p-3 space-y-3">
              <p className="text-sm">
                Send this {label(item.channel)} to <span className="font-medium">{item.address}</span>:
              </p>
              {isEmail && <p className="text-sm font-medium">{subject}</p>}
              <p className="text-sm whitespace-pre-wrap">{isEmail ? body : item.body}</p>
              {isEmail && (
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor={`from-${rowKey}`}>
                    Send from
                  </label>
                  <select
                    id={`from-${rowKey}`}
                    aria-label="Send from"
                    className={input}
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  >
                    <option value="">Choose a sender</option>
                    {fromEmails.map((email) => (
                      <option key={email} value={email}>
                        {email}
                      </option>
                    ))}
                  </select>
                  {from && <p className="text-sm mt-2">From: {from}</p>}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  className={primaryBtn}
                  disabled={busy || blockedByPlaceholders || (isEmail && !from)}
                  onClick={confirm}
                >
                  Confirm
                </button>
                <button className={secondaryBtn} disabled={busy} onClick={() => setConfirming(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {item.suggest_park && onPark && (
        <div>
          {parkOn === null ? (
            <button
              className={secondaryBtn}
              disabled={busy}
              onClick={() => setParkOn(nextJanuaryFifth(new Date(Date.now())))}
            >
              Park this partner
            </button>
          ) : (
            <div className="border border-border rounded-lg p-3 space-y-3">
              <p className="text-sm">
                Move {item.name} to Parked with the next action &quot;{PARK_NEXT_ACTION}&quot; on {formatDay(parkOn)}?
              </p>
              <div className="flex gap-2">
                <button
                  className={primaryBtn}
                  disabled={busy}
                  onClick={async () => {
                    await onPark(item, parkOn);
                    setParkOn(null);
                  }}
                >
                  Confirm park
                </button>
                <button className={secondaryBtn} disabled={busy} onClick={() => setParkOn(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Per-partner outreach drafts to review and send, one at a time. */
const OutreachTab = () => {
  const toast = useToast();
  const [data, setData] = useState(/** @type {any} */ (null));
  const [busyId, setBusyId] = useState(/** @type {string|null} */ (null));
  const [testingId, setTestingId] = useState(/** @type {string|null} */ (null));

  // Only the newest response may be applied: every load and every send bumps seqRef, so a response that
  // started before a send (or arrived out of order) is dropped instead of reverting a just-sent row.
  const seqRef = useRef(0);
  const inFlightRef = useRef(false);
  const busyRef = useRef(/** @type {string|null} */ (null));
  const testingRef = useRef(/** @type {string|null} */ (null));

  const load = useCallback(
    async ({ poll = false } = {}) => {
      if (poll && (inFlightRef.current || busyRef.current)) return;
      seqRef.current += 1;
      const mine = seqRef.current;
      inFlightRef.current = true;
      try {
        const next = await adminWholesaleService.listOutreach();
        if (mine === seqRef.current) setData(next);
      } catch (err) {
        if (mine === seqRef.current) toast.error(err.message || 'Failed to load the outreach queue');
      } finally {
        if (mine === seqRef.current) inFlightRef.current = false;
      }
    },
    [toast],
  );

  useEffect(() => {
    load();
  }, [load]);

  const followups = (data && data.followups) || [];
  const anyQueued = !!data && [...data.items, ...followups].some((i) => i.status === WHOLESALE_OUTREACH_STATUS.QUEUED);
  useEffect(() => {
    if (!anyQueued) return undefined;
    const timer = setInterval(() => load({ poll: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [anyQueued, load]);

  // Emails the draft to the logged-in admin only; records nothing, so the queue is left as it is.
  const test = async (item, edits, fromEmail) => {
    if (busyRef.current || testingRef.current) return;
    testingRef.current = item.partner_id;
    setTestingId(item.partner_id);
    try {
      const res = await adminWholesaleService.testSendOutreach(item.partner_id, {
        subject: edits.subject,
        body: edits.body,
        from_email: fromEmail,
      });
      toast.success(`Test email sent to ${res.sent_to}`);
    } catch (err) {
      toast.error(err.message || 'Could not send the test email');
    } finally {
      testingRef.current = null;
      setTestingId(null);
    }
  };

  // Submits the missing wholesale templates to Meta. Same one-action-at-a-time lock; the strip refreshes after.
  const submitTemplates = async () => {
    if (busyRef.current || testingRef.current) return;
    busyRef.current = 'templates';
    seqRef.current += 1;
    inFlightRef.current = false;
    setBusyId('templates');
    try {
      const res = await adminWholesaleService.submitWhatsAppTemplates();
      (res.results || []).forEach((r) => {
        if (r.outcome === 'failed') toast.error(`${r.name}: ${r.detail || 'could not be submitted'}`);
        else toast.success(`${r.name} ${OUTCOME_TOAST[r.outcome] || r.outcome}`);
      });
    } catch (err) {
      toast.error(err.message || 'Could not submit the templates');
    } finally {
      seqRef.current += 1;
      busyRef.current = null;
      setBusyId(null);
    }
    await load();
  };

  // Records a message the operator sent by hand. Same one-action-at-a-time lock as a send.
  const markSent = async (item, channel, note) => {
    if (busyRef.current || testingRef.current) return;
    busyRef.current = item.partner_id;
    seqRef.current += 1;
    inFlightRef.current = false;
    setBusyId(item.partner_id);
    try {
      await adminWholesaleService.markOutreachSent(item.partner_id, { channel, note });
      toast.success(`${item.name} marked as contacted`);
    } catch (err) {
      toast.error(err.message || 'Could not mark as sent');
    } finally {
      seqRef.current += 1;
      busyRef.current = null;
      setBusyId(null);
    }
    // Success removes the row; a refusal means things changed: either way show the queue as it is now.
    await load();
  };

  // Records that the operator submitted a web form or application by hand. Same lock and reload as Mark as sent.
  const markSubmitted = async (item, note) => {
    if (busyRef.current || testingRef.current) return;
    busyRef.current = item.partner_id;
    seqRef.current += 1;
    inFlightRef.current = false;
    setBusyId(item.partner_id);
    try {
      await adminWholesaleService.markOutreachSubmitted(item.partner_id, { note });
      toast.success(`${item.name} marked as submitted`);
    } catch (err) {
      toast.error(err.message || 'Could not mark as submitted');
    } finally {
      seqRef.current += 1;
      busyRef.current = null;
      setBusyId(null);
    }
    // Success removes the row; a refusal means things changed: either way show the queue as it is now.
    await load();
  };

  // One path for a first message and a follow-up: the same lock, stale-response guard and cap counting.
  const performSend = async (item, edits, fromEmail) => {
    if (testingRef.current || busyRef.current) return;
    const followup = !!FOLLOWUP_LABEL[item.kind];
    const key = rowKeyOf(item);
    busyRef.current = key;
    seqRef.current += 1;
    inFlightRef.current = false;
    setBusyId(key);
    let reload = false;
    try {
      // channel and address are what the operator reviewed: the server refuses if the contacts moved since.
      const payload = {
        subject: edits.subject,
        body: edits.body,
        contact_id: item.contact_id,
        channel: item.channel,
        address: item.address,
      };
      const body = fromEmail ? { ...payload, from_email: fromEmail } : payload;
      const updated = followup
        ? await adminWholesaleService.sendFollowup(item.partner_id, item.kind, body)
        : await adminWholesaleService.sendOutreach(item.partner_id, body);
      const replace = (list) => list.map((i) => (sameRow(i, item) ? updated : i));
      setData(
        (prev) =>
          prev && {
            ...prev,
            // The server counted this send (sent, queued or sending): keep the visible total in step.
            daily_cap: bumpCap(prev.daily_cap, updated.status),
            ...(followup ? { followups: replace(prev.followups || []) } : { items: replace(prev.items) }),
          },
      );
    } catch (err) {
      toast.error(err.message || 'Could not send');
      reload = true;
    } finally {
      seqRef.current += 1;
      busyRef.current = null;
      setBusyId(null);
    }
    // The server may have stored a failure (or refused because things changed): show the row as it is now.
    if (reload) await load();
  };

  // Parks a partner after the final line: through the existing stage and partner endpoints, only on confirm.
  const park = async (item, parkOn) => {
    if (testingRef.current || busyRef.current) return;
    const key = rowKeyOf(item);
    busyRef.current = key;
    seqRef.current += 1;
    inFlightRef.current = false;
    setBusyId(key);
    let reload = false;
    try {
      await adminWholesaleService.changeStage(item.partner_id, WHOLESALE_STAGE.PARKED);
      try {
        await adminWholesaleService.updatePartner(item.partner_id, {
          next_action: PARK_NEXT_ACTION,
          next_action_on: parkOn,
        });
        toast.success(`${item.name} parked; next action: ${PARK_NEXT_ACTION}`);
      } catch (err) {
        toast.error(`${item.name} parked, but the next action could not be saved: ${err.message || 'try again'}`);
        reload = true;
      }
      // A parked partner is no longer offered any follow-up.
      setData(
        (prev) =>
          prev && { ...prev, followups: (prev.followups || []).filter((i) => i.partner_id !== item.partner_id) },
      );
    } catch (err) {
      toast.error(err.message || 'Could not park this partner');
    } finally {
      seqRef.current += 1;
      busyRef.current = null;
      setBusyId(null);
    }
    if (reload) await load();
  };

  if (!data) return <div className={`${card} p-6 text-sm text-muted-foreground`}>Loading the outreach queue...</div>;

  return (
    <div className="space-y-4">
      {!data.whatsapp_enabled && (
        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3">
          WhatsApp sending is off on this environment
        </div>
      )}
      {data.email_enabled === false && (
        <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3">
          Email sending is off on this environment
        </div>
      )}
      {data.daily_cap && (
        <div className="text-sm text-muted-foreground" data-testid="daily-cap">
          Today: {data.daily_cap.used} of {data.daily_cap.cap} sent
        </div>
      )}
      <TemplatesStrip
        states={data.whatsapp_templates}
        configured={data.whatsapp_configured !== false}
        busy={busyId !== null || testingId !== null}
        onSubmit={submitTemplates}
      />
      {data.items.length === 0 && (
        <div className={`${card} p-12 text-center text-muted-foreground`}>Nothing to send right now.</div>
      )}
      {data.items.map((item) => (
        <OutreachRow
          key={item.partner_id}
          item={item}
          fromEmails={data.from_emails}
          busyId={busyId}
          testingId={testingId}
          capReached={!!data.daily_cap && data.daily_cap.remaining <= 0}
          onSend={performSend}
          onTest={test}
          onMark={markSent}
          onSubmit={markSubmitted}
        />
      ))}
      <div className="space-y-4" data-testid="followups">
        <h3 className="text-base font-medium">Follow-ups</h3>
        {followups.length === 0 && (
          <div className={`${card} p-8 text-center text-muted-foreground`}>No follow-ups due</div>
        )}
        {followups.map((item) => (
          <OutreachRow
            key={rowKeyOf(item)}
            item={item}
            fromEmails={data.from_emails}
            busyId={busyId}
            testingId={testingId}
            capReached={!!data.daily_cap && data.daily_cap.remaining <= 0}
            onSend={performSend}
            onTest={test}
            onPark={park}
          />
        ))}
      </div>
    </div>
  );
};

export default OutreachTab;
